import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

export const buttonVariants = cva(
  "group relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-slate-900/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[1.1em]",
  {
    variants: {
      variant: {
        // Slate-900 primary CTA.
        primary:
          'bg-slate-900 text-white shadow-soft-sm hover:bg-slate-700 active:bg-slate-500',
        // Rare warm accent.
        accent:
          'bg-ember-500 text-white shadow-soft-sm hover:bg-ember-600 active:bg-ember-700',
        secondary:
          'border border-hairline bg-white text-ink-900 shadow-soft-sm hover:bg-ink-50 active:bg-ink-100',
        outline:
          'border border-ink-200 bg-transparent text-ink-900 hover:bg-ink-50 active:bg-ink-100',
        ghost: 'text-ink-600 hover:bg-ink-50 hover:text-ink-900',
        link: 'h-auto rounded-none p-0 text-ink-900 underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        default: 'h-11 px-5 text-sm',
        lg: 'h-12 px-7 text-[0.95rem]',
        icon: 'size-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

type ButtonBaseProps = VariantProps<typeof buttonVariants> & {
  className?: string;
};

type ButtonAsButton = ButtonBaseProps &
  Omit<React.ComponentPropsWithoutRef<'button'>, 'className'> & {
    href?: undefined;
  };

type ButtonAsLink = ButtonBaseProps &
  Omit<React.ComponentPropsWithoutRef<'a'>, 'className'> & {
    href: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

/**
 * Brand button. Renders an anchor when `href` is provided (for CTAs) and a
 * native button otherwise. All styling flows through `buttonVariants` so the
 * look stays consistent and centrally editable.
 */
export function Button({ className, variant, size, ...props }: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);

  if ('href' in props && props.href !== undefined) {
    return <a className={classes} {...(props as ButtonAsLink)} />;
  }
  return <button className={classes} {...(props as ButtonAsButton)} />;
}
